import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { BookingStatus } from 'angular/app/core/models/entities/booking';
import { Observable } from 'rxjs';
import { Room } from '../../../core/models/entities/room';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  BookingsDisplayMode,
  BookingsScope,
} from 'angular/app/core/store/bookings/bookings.state';
import { fromStore } from '../../../core/store';
import {
  addDays,
  differenceInMinutes,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { bookingsActions } from 'angular/app/core/store/bookings';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { ModalService } from '../../../core/services/modal.service';
import { CreateDuplicateRoomModalComponent } from '../../../rooms/components/create-room-modal/create-duplicate-room-modal.component';
import { InputOptionsHelper } from '../../../core/helpers/input-options.helper';
import {
  selectAllRooms,
  selectBookingsRoomsFilter,
} from '../../../core/store/reducers';
import { RoomPermission } from '../../../core/models/entities/permission';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-bookings-calendar-view',
  template: `
    <div class="mt-6 lg:hidden">
      <app-table-filter
        label="Nom de la salle"
        [selectedValues]="(roomsFilterValues$ | async) ?? []"
        [options]="roomsOptions$ | async"
        (selectionChanged)="roomsFilterChanged($event)"
      ></app-table-filter>
    </div>
    
    <div class="relative mt-16">
      <div class="flex" [class.hidden]="!rooms().length">
        <div class="relative w-1/5" #roomsNameContainer>
          <div
            class="absolute -top-7 z-10 hidden lg:block"
            [style.transform]="
              'translateY(-' +
              (displayMode() === BookingsDisplayMode.Week ? 1 : 0) +
              'rem)'
            "
            >
            <app-table-filter
              label="Nom de la salle"
              [selectedValues]="(roomsFilterValues$ | async) ?? []"
              [options]="roomsOptions$ | async"
              (selectionChanged)="roomsFilterChanged($event)"
            ></app-table-filter>
          </div>
        </div>
    
        <div class="relative grow" #calendarContainer>
          @if (displayMode() === BookingsDisplayMode.Day) {
            <div
              class="relative block"
            [ngStyle]="{
              transform: 'translateX(-' + calendarBlockWidthInPx() / 2 + 'px)',
            }"
              >
              @for (hour of dayHours(); track hour; let i = $index; let last = $last) {
                <div
                  [style.width]="calendarBlockWidthInPx() + 'px'"
                  [style.left]="calendarBlockWidthInPx() * i + 'px'"
                  class="absolute -top-6 text-center text-xs"
                  >
                  {{ !last ? hour : '0:00' }}
                </div>
              }
            </div>
          }
          @for (date of currentWeekDates(); track date; let i = $index) {
            <div
              [style.width]="100 / currentWeekDates()?.length + '%'"
              [style.left]="(100 / currentWeekDates()?.length) * i + '%'"
            [style.transform]="
              'translateY(-' +
              (displayMode() === BookingsDisplayMode.Week ? 2.75 : 0) +
              'rem)'
            "
              class="text-primary-500 absolute text-center"
              [class.h-16]="displayMode() === BookingsDisplayMode.Week"
              [class.overflow-hidden]="displayMode() === BookingsDisplayMode.Week"
              >
              @if (displayMode() === BookingsDisplayMode.Week) {
                <div
                  class="lg:text-normal text-secondary-500 py-2 text-xs"
              [ngClass]="{
                'bg-primary-500 text-white': isToday(date),
                'text-secondary-500': !isToday(date),
              }"
                  >
                  {{ date | date: 'EE dd MMM' }}
                </div>
              }
              @if (isToday(date)) {
                <div
                  class="relative flex h-6 w-12 flex-col items-center overflow-visible text-center"
                  [style.left]="'calc(' + timeLeftOffsetForDate + '% - 24px)'"
                  >
                  <div class="absolute bottom-10">
                    @if (displayMode() === BookingsDisplayMode.Day) {
                      <div
                        class="text-xs leading-none font-medium"
                        >{{ time | date: 'HH:mm' }}</div
                        >
                      }
                      <div
                        class="mt-1 leading-none"
                  [ngStyle]="{
                    height: '12px',
                    transform:
                      displayMode() === BookingsDisplayMode.Day
                        ? 'translateY(-12px)'
                        : 'translateY(20px)',
                  }"
                        ><i class="fas fa-sort-down"></i
                      ></div>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
    
        @if (loadingRooms() && !rooms()?.length) {
          <div class="flex h-64 items-center justify-center">
            <app-loader></app-loader>
          </div>
        } @else if (!rooms()?.length) {
          <div class="flex h-64 items-center justify-center">
            <ng-template [appHasPermission]="RoomPermission.Store">
              <app-empty-state
                [message]="emptyRoomsText$ | async"
                icon="house"
                [showAddButton]="noRoomsFilters$ | async"
                (buttonClicked)="createRoom()"
              ></app-empty-state>
            </ng-template>
    
            <ng-template [appHasNoPermission]="RoomPermission.Store">
              <app-empty-state
                message="Vous n'avez accès à aucune salle pour le moment."
                icon="house"
                [showAddButton]="false"
              ></app-empty-state>
            </ng-template>
          </div>
        } @else {}
    
          @for (room of rooms(); track room.id) {
            <div
              class="group flex h-14 odd:bg-white even:bg-neutral-100"
              data-cy="room-row"
              >
              <app-booking-calendar-room
                class="flex grow"
                [room]="room"
                [scope]="scope()"
                />
            </div>
          }
    
          <!-- Ligne qui représente l'heure actuelle dans la semaine -->
          <div
            class="text-primary-500 absolute left-0 z-10"
        [ngClass]="{
          '-top-8 h-[calc(100%+2rem)]':
            displayMode() === BookingsDisplayMode.Day,
          '-top-1 h-[calc(100%+0.25rem)]':
            displayMode() === BookingsDisplayMode.Week,
          hidden: !rooms().length,
        }"
            [style.transform]="'translateX(' + roomsNameContainerWidth() + 'px)'"
            >
            @if (isTimeBarVisible()) {
              <div
                class="relative h-full w-0.5 text-center"
            [style.left]="
              (timeBarLeftOffsetInPercentage() * calendarWidth()) / 100 -
              1 +
              'px'
            "
                >
                <div
                  class="border-primary-500 mx-auto h-full w-0 border-r-2 border-dashed"
                  >
                </div>
              </div>
            }
            @if (loadingRooms() === false && (loadingBookings$ | async)) {
              <div
                class="absolute top-0 left-0 flex h-full items-center justify-center text-black"
                [style.width]="calendarWidth() + 'px'"
                >
                <app-loader></app-loader>
              </div>
            }
          </div>
        </div>
    `,
  standalone: false,
})
export class BookingsCalendarViewComponent
  extends DestroyableComponent
  implements OnInit, AfterViewInit
{
  platformId = inject(PLATFORM_ID);
  scope = input<BookingsScope>();
  @ViewChild('calendarContainer') calendarContainer: ElementRef;
  @ViewChild('roomsNameContainer') roomsNameContainer: ElementRef;
  loadingRooms: Signal<boolean> = this.store.selectSignal(
    fromStore.selectRoomsLoading,
  );
  loadingBookings$: Observable<boolean>;
  noRoomsFilters$: Observable<boolean>;
  emptyRoomsText$: Observable<string>;
  allRooms = this.store.selectSignal(selectAllRooms);
  roomsFilters = computed(() =>
    this.scope()
      ? this.store.selectSignal(selectBookingsRoomsFilter(this.scope()))()
      : null,
  );
  rooms: Signal<Room[]> = computed(() => {
    return this.allRooms()
      .filter((room) => room.active)
      .filter(
        (room) =>
          !this.roomsFilters()?.ids ||
          this.roomsFilters()?.ids.length === 0 ||
          this.roomsFilters()?.ids.includes(room.id),
      )
      .sort((a, b) =>
        a.active === b.active ? a.position - b.position : a.active ? -1 : 1,
      );
  });
  displayMode: Signal<BookingsDisplayMode>;
  calendarDate: Signal<Date>;
  isTimeBarVisible: Signal<boolean>;
  currentWeekDates: Signal<string[]>;
  calendarWidth = signal(0);
  roomsNameContainerWidth = signal(0);
  calendarBlockWidthInPx: Signal<number>;
  dayHours: Signal<string[]> = computed(() =>
    this.dayBlocksPer(24 / this.numberOfBlocksPerDay(this.calendarWidth())),
  );
  timeBarLeftOffsetInPercentage: Signal<number>;
  roomsFilterValues$: Observable<number[]>;
  roomsOptions$ = InputOptionsHelper.selectOptions$(
    this.store
      .select(selectAllRooms)
      .pipe(map((rooms) => rooms.filter((room) => room.active))),
  );

  BookingsDisplayMode = BookingsDisplayMode;
  BookingStatus = BookingStatus;
  RoomPermission = RoomPermission;

  constructor(
    private store: Store,
    private modalService: ModalService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();

    /**
     * Si écran <= md on passe en mode jour car pas assez de place pour afficher la semaine
     */
    afterNextRender(() => {
      const mql = window.matchMedia('(max-width: 768px)');
      if (mql.matches) {
        this.store.dispatch(
          bookingsActions.setDisplayMode({
            displayMode: BookingsDisplayMode.Day,
            scope: this.scope(),
          }),
        );
      }
      mql.addEventListener('change', (e) => {
        if (e.matches) {
          this.store.dispatch(
            bookingsActions.setDisplayMode({
              displayMode: BookingsDisplayMode.Day,
              scope: this.scope(),
            }),
          );
        }
      });
    });
  }

  ngOnInit() {
    this.loadingBookings$ = this.store.select(
      fromStore.selectBookingsLoading(this.scope()),
    );
    this.noRoomsFilters$ = this.store
      .select(fromStore.selectBookingsRoomsFilter(this.scope()))
      .pipe(map((filter) => !filter.people && !filter.eventTypeId));
    this.roomsFilterValues$ = this.store
      .select(fromStore.selectBookingsState)
      .pipe(
        map((bookingsState) => bookingsState[this.scope()].roomsFilter.ids),
      );
    this.emptyRoomsText$ = this.store
      .select(fromStore.selectBookingsRoomsFilter(this.scope()))
      .pipe(
        map((filter) =>
          !filter.people && !filter.eventTypeId
            ? 'Ajoutez une salle pour commencer'
            : "Il n'y a pas de salles correspondant à votre recherche.",
        ),
      );

    this.displayMode = this.store.selectSignal(
      fromStore.selectBookingsDisplayMode(this.scope()),
    );
    this.calendarDate = this.store.selectSignal(
      fromStore.selectBookingsCalendarDate(this.scope()),
    );

    this.calendarBlockWidthInPx = computed(() => {
      const numberOfBlocks =
        this.displayMode() === BookingsDisplayMode.Week
          ? 7
          : this.numberOfBlocksPerDay(this.calendarWidth());
      return this.calendarWidth() / numberOfBlocks;
    });

    this.timeBarLeftOffsetInPercentage = computed(() =>
      this.displayMode() === BookingsDisplayMode.Week
        ? (differenceInMinutes(
            new Date(),
            startOfWeek(new Date(), { weekStartsOn: 1 }),
          ) /
            (24 * 60 * 7)) *
          100
        : (differenceInMinutes(new Date(), startOfDay(new Date())) /
            (24 * 60)) *
          100,
    );

    this.isTimeBarVisible = computed(() =>
      this.displayMode() === BookingsDisplayMode.Day
        ? isSameDay(this.calendarDate(), new Date())
        : isAfter(
            this.calendarDate(),
            startOfWeek(new Date(), { weekStartsOn: 1 }),
          ) &&
          isBefore(
            this.calendarDate(),
            endOfWeek(new Date(), { weekStartsOn: 1 }),
          ),
    );
    this.currentWeekDates = computed(() => {
      const dates: string[] = [];
      if (this.displayMode() === BookingsDisplayMode.Week) {
        for (let i = 0; i < 7; i++) {
          dates.push(
            format(
              addDays(startOfWeek(this.calendarDate(), { weekStartsOn: 1 }), i),
              'yyyy-MM-dd',
            ),
          );
        }
      } else if (this.displayMode() === BookingsDisplayMode.Day) {
        dates.push(format(this.calendarDate(), 'yyyy-MM-dd'));
      }

      return dates;
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      const resizeObserver = new ResizeObserver((entries) => {
        this.calendarWidth.set(entries[0].contentRect.width);
        this.roomsNameContainerWidth.set(entries[1].contentRect.width);
      });

      resizeObserver.observe(this.calendarContainer?.nativeElement);
      resizeObserver.observe(this.roomsNameContainer?.nativeElement);

      this.changeDetectorRef.detectChanges();
    }
  }

  isToday(date) {
    return isSameDay(new Date(), new Date(date));
  }

  get timeLeftOffsetForDate() {
    return (
      (differenceInMinutes(new Date(), startOfDay(new Date())) / (24 * 60)) *
      100
    );
  }

  numberOfBlocksPerDay(width: number) {
    const minWidth = 60;

    const hoursPerBlock = [24, 12, 8, 6, 4, 3, 2, 1];

    for (const hours of hoursPerBlock) {
      if (width / hours >= minWidth) {
        return hours;
      }
    }

    return 1;
  }

  dayBlocksPer(hours): string[] {
    const blocks = [];
    for (let i = 0; i <= 24; i += hours) {
      blocks.push(i + ':00');
    }
    return blocks;
  }

  get time() {
    return new Date();
  }

  createRoom() {
    this.modalService.open(CreateDuplicateRoomModalComponent, {
      title: 'Ajouter une salle',
      okAction: {
        title: 'Enregistrer',
      },
    });
  }

  roomsFilterChanged(values) {
    this.store.dispatch(
      bookingsActions.setRoomsFilter({
        filter: { ids: values },
        scope: this.scope(),
      }),
    );
  }
}
